import { cn } from '../../utils/cn';

interface SkeletonLoaderProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular' | 'card';
  width?: string | number;
  height?: string | number;
  lines?: number;
}

export const SkeletonLoader = ({
  className,
  variant = 'rectangular',
  width,
  height,
  lines = 1,
}: SkeletonLoaderProps) => {
  const baseClasses = 'bg-gray-200 relative overflow-hidden';
  
  const variantClasses = {
    text: 'h-4 rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
    card: 'rounded-2xl',
  };

  const style = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  if (variant === 'text' && lines > 1) {
    return (
      <div className={cn('space-y-2', className)}>
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            className={cn(baseClasses, variantClasses.text)}
            style={{
              ...style,
              width: index === lines - 1 ? '80%' : '100%',
            }}
          >
            <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/40 to-transparent" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(baseClasses, variantClasses[variant], className)}
      style={style}
    >
      <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/40 to-transparent" />
    </div>
  );
};

// Card skeleton for sector cards
export const SectorCardSkeleton = () => (
  <div className="bg-white rounded-2xl p-8 shadow-sm">
    <div className="flex items-start justify-between mb-6">
      <SkeletonLoader variant="circular" width={48} height={48} />
      <SkeletonLoader variant="circular" width={20} height={20} />
    </div>
    <SkeletonLoader variant="text" lines={1} height={24} className="mb-4" />
    <SkeletonLoader variant="text" lines={3} height={16} />
  </div>
);

// Image skeleton
export const ImageSkeleton = ({ className }: { className?: string }) => (
  <SkeletonLoader
    variant="rectangular"
    className={cn('w-full h-full', className)}
  />
);
